import type { PaymentFailureCode, PaymentFailureReason } from 'paintscout'
import { refineUnknownReason } from './payment-failure-reason'

// Non-reason entries that share the legacy `events` / `failedEvents` arrays
const NON_REASON_EVENTS = ['fully paid', 'still owing', 'Code 200 Error']

// Loose enough to take both the document types and the generated GraphQL types (string code, nullable fields)
interface FailureReasonLike {
  code: string
  providerCode?: string | null
  message?: string | null
}

// Takes any activity's details, so callers need no cast to PaymentActivityDetails
type FailureReasonActivityDetails = { failureReason?: FailureReasonLike | null; events?: string[] | null }

export interface FailureReasonSource {
  activity?: { details?: FailureReasonActivityDetails | null } | null
  payment?: { failureReason?: FailureReasonLike | null; failedEvents?: string[] | null } | null
}

/** The structured `failureReason` stamped on an activity (#20588), without the legacy `events` fallback. */
export function getStructuredFailureReason(details?: FailureReasonActivityDetails | null): FailureReasonLike | null {
  return details?.failureReason ?? null
}

/**
 * Resolves the failure reason for a payment, preferring the structured `failureReason` (activity first, since it
 * carries the raw message) and falling back to the legacy string arrays written before #20588.
 */
export function getFailureReason({ activity, payment }: FailureReasonSource): PaymentFailureReason | null {
  const structured = activity?.details?.failureReason ?? payment?.failureReason
  if (structured?.code) {
    return refineUnknownReason({
      code: structured.code as PaymentFailureCode,
      ...(structured.providerCode && { providerCode: structured.providerCode }),
      ...(structured.message && { message: structured.message })
    })
  }

  const reasonsOnly = (events?: string[] | null) =>
    (events ?? []).filter((event) => !!event && !NON_REASON_EVENTS.includes(event))
  const fromActivity = reasonsOnly(activity?.details?.events)
  const legacy = fromActivity.length ? fromActivity : reasonsOnly(payment?.failedEvents)
  if (!legacy.length) return null
  return refineUnknownReason<PaymentFailureReason>({ code: 'unknown', message: legacy.join(' · ') })
}
